import { SubmissionErrorBoundary } from '@/components/errors/SubmissionErrorBoundary';
import { TsForm } from '@/components/form/Form';
import { TimeField } from '@/components/form/TimeInput';
import { Button } from '@/components/shared/button/Button';
import { dateToApiFormat, secondsToTimeString, timeStringToSecondsSinceMidnight } from '@/helpers/dateHelper';
import { useConfirm } from '@/services/confirm/ConfirmationService';
import { EmployeeSchedule, EmployeeScheduleDay } from '@/services/employee/schedule/schedule.types';
import { strings } from '@/services/translation/strings';
import { FC, useEffect } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import { useForm } from 'react-hook-form';

interface FormValues {
  startTime: string;
  endTime: string;
}

interface AvailabilityFormProps {
  date: Date;
  existingDay?: EmployeeScheduleDay;
  fetching: boolean;
  onSave: (schedule: EmployeeSchedule) => Promise<void>;
  onCancel: () => void;
}

export const AvailabilityForm: FC<AvailabilityFormProps> = ({ date, existingDay, fetching, onSave, onCancel }) => {
  const handleError = useErrorHandler();

  const availableTimes = existingDay?.availableTimes[0];

  const startTime = secondsToTimeString(availableTimes?.start ?? existingDay?.patternStart ?? 0);
  const endTime = secondsToTimeString(availableTimes?.end ?? existingDay?.patternEnd ?? 0);

  const methods = useForm<FormValues>({
    defaultValues: { startTime, endTime }
  });

  const { registerForConfirmation, unregisterForConfirmation } = useConfirm(state => ({
    registerForConfirmation: state.registerForConfirmation,
    unregisterForConfirmation: state.unregisterForConfirmation
  }));

  const {
    formState: { isDirty }
  } = methods;

  useEffect(() => {
    if (isDirty) {
      registerForConfirmation(strings.settings.unsaved);
    } else {
      unregisterForConfirmation();
    }
  }, [isDirty]);

  useEffect(() => () => unregisterForConfirmation(), []);

  useEffect(() => {
    methods.reset({ startTime, endTime });
  }, [startTime, endTime]);

  const onSubmit = async (values: FormValues) => {
    const dateKey = dateToApiFormat(date);

    const availableTimes = [
      {
        start: timeStringToSecondsSinceMidnight(values.startTime),
        end: timeStringToSecondsSinceMidnight(values.endTime)
      }
    ];

    const schedule: EmployeeSchedule = {
      [dateKey]: {
        date,
        leaveType: undefined,
        availableTimes,
        shifts: existingDay?.shifts ?? [],
        patternStart: existingDay?.patternStart,
        patternEnd: existingDay?.patternEnd
      }
    };

    try {
      await onSave(schedule);
    } catch (ex) {
      handleError(ex);
    }
  };

  return (
    <SubmissionErrorBoundary>
      <TsForm methods={methods} onSubmit={onSubmit}>
        <div className="flex flex-col space-y-5">
          <div className="flex flex-row space-x-2">
            <TimeField
              className="flex-1"
              id="startTime"
              name="startTime"
              label={strings.timeshift.teamMember.calendarDayView.editAvailability.availabilityStart}
            />
            <TimeField
              className="flex-1"
              id="endTime"
              name="endTime"
              label={strings.timeshift.teamMember.calendarDayView.editAvailability.availabilityEnd}
            />
          </div>
          <div className="flex w-full items-center justify-end gap-5">
            <Button className="flex-1" onClick={onCancel}>
              {strings.common.cancel}
            </Button>
            <Button className="flex-1" primary type="submit" loading={fetching}>
              {strings.common.save}
            </Button>
          </div>
        </div>
      </TsForm>
    </SubmissionErrorBoundary>
  );
};
